import { FormControl, styled } from "@mui/material";
import type { Control, FieldErrors, FieldValues, Path } from "react-hook-form";
import { Controller } from "react-hook-form";
import { DateField } from "@mui/x-date-pickers/DateField";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { esES } from "@mui/x-date-pickers/locales";
import es from "date-fns/locale/es";
import { registerLocale } from "react-datepicker";
import { parseDateOnly } from "@mekidoc/utils";
import { FormErrorMessage, InputLabel, RelativeContainer } from "./FormStyledComponents";

registerLocale("es", es);

const StyledDateField = styled(DateField, {
  shouldForwardProp: (prop) => prop !== "error",
})<{ error: string | undefined }>(({ error, theme }) => ({
  fontFamily: "Poppins",
  fontSize: "16px",
  background: theme.palette.background.paper,
  ...(error && {
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: `${theme.palette.error.main} !important`,
    },
  }),
  ".Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: `${theme.palette.primary.main} !important`,
  },
  "& .MuiIconButton-root": {
    color: theme.palette.icon.primary,
  },
}));

interface BirthdateAttribute extends FieldValues {
  birthdate: string;
}

interface BirthdateInputProps<T extends BirthdateAttribute> {
  control: Control<T>;
  errorManagement: {
    errors: FieldErrors<T>;
  };
  obscureInTracker?: boolean;
}

export const BirthdateInput = <T extends BirthdateAttribute>({
  control,
  errorManagement: { errors },
  obscureInTracker = false,
}: BirthdateInputProps<T>) => {
  const inputName = "birthdate" as Path<T>;
  const errorMessage = errors.birthdate?.message?.toString();

  return (
    <FormControl fullWidth>
      <InputLabel>Fecha de nacimiento</InputLabel>
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={es}
        localeText={esES.components.MuiLocalizationProvider.defaultProps.localeText}
      >
        <Controller
          name={inputName}
          control={control}
          defaultValue={undefined}
          rules={{
            required: "Debes seleccionar una fecha.",
            validate: (value) => {
              const date = new Date(value);

              if (date.toString() === "Invalid Date") {
                return "Fecha incompleta o inválida";
              }

              if (date.getFullYear() < 1900) {
                return "La fecha es demasiado antigua";
              }

              if (date > new Date()) {
                return "La fecha ingresada aún no ha ocurrido";
              }

              return true;
            },
          }}
          render={({ field: { onChange, value } }) => (
            <StyledDateField
              value={value && value.length >= 8 ? parseDateOnly(value) : null}
              format="dd/MM/yyyy"
              clearable={true}
              minDate={new Date("1900-01-01")}
              maxDate={new Date()}
              onChange={(date) => onChange(date)}
              error={errorMessage}
              slotProps={{
                textField: {
                  ...(errorMessage && {
                    focused: false,
                  }),
                  ...(obscureInTracker && {
                    inputProps: {
                      "data-openreplay-obscured": "",
                    },
                  }),
                },
              }}
            />
          )}
        />
      </LocalizationProvider>
      {errorMessage && (
        <RelativeContainer>
          <FormErrorMessage>{errorMessage}</FormErrorMessage>
        </RelativeContainer>
      )}
    </FormControl>
  );
};
